import { useCallback, useMemo, useRef } from 'react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import type { ConversationMessagePersona } from '@duke-hq/ui/components/Conversation/ConversationMessage';

const PERSONA_STALE_TIME_MS = 60 * 10 * 1000;
const DUKE_FALLBACK: ConversationMessagePersona = { name: 'Duke', avatarUrl: null };

// -- useGetPersonaById ---------------------------------------------------------
// Resolves a persona to the shape ConversationMessage renders, shared by both
// previews. Personas are read through a ref so the returned resolver is stable
// even though the editor captures it before the query resolves. Gate the preview
// start on `personasReady`, otherwise the first block renders the Duke fallback
// and won't re-render once the real persona arrives.
export function useGetPersonaById(): {
  getPersonaById: (personaId: string) => ConversationMessagePersona;
  personasReady: boolean;
} {
  const { data: contentPersonas } = trpcAdmin.contentPersonas.all.useQuery(undefined, {
    staleTime: PERSONA_STALE_TIME_MS,
  });

  const personasById = useMemo(() => {
    const map = new Map<string, ConversationMessagePersona>();
    contentPersonas?.forEach((p) => map.set(p.id, { name: p.name, avatarUrl: p.avatarUrl }));
    return map;
  }, [contentPersonas]);

  const personasByIdRef = useRef(personasById);
  personasByIdRef.current = personasById;

  const getPersonaById = useCallback(
    (personaId: string): ConversationMessagePersona =>
      personasByIdRef.current.get(personaId) ?? DUKE_FALLBACK,
    [],
  );

  const personasReady = contentPersonas !== undefined;

  return { getPersonaById, personasReady };
}
